import apiClient from './api';
import { 
  ZulassungsantragResponse, 
  ZulassungsantragListItem,
  ZulassungsantragCreateRequest,
  ZulassungsantragFormData,
  StatusAbfrageResult,
  SubmitResult
} from '../types/antrag';

export interface PaginatedZulassungsantraegeResponse {
  content: ZulassungsantragListItem[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
}

export class ZulassungsantragService {
  private static readonly BASE_URL = '/zulassungsantraege';

  // Alle Zulassungsanträge abrufen (mit Pagination)
  static async getAlleZulassungsantraege(
    page: number = 0, 
    size: number = 10,
    sortBy: string = 'erstelltAm',
    sortDir: string = 'desc',
    fin?: string,
    kennzeichen?: string,
    status?: string
  ): Promise<PaginatedZulassungsantraegeResponse> {
    const params: Record<string, any> = { page, size, sortBy, sortDir };
    if (fin?.trim()) params.fin = fin.trim();
    if (kennzeichen?.trim()) params.kennzeichen = kennzeichen.trim();
    if (status?.trim()) params.status = status.trim();
    const response = await apiClient.get(this.BASE_URL, { params });
    return response.data;
  }

  // Zulassungsantrag nach ID abrufen
  static async getZulassungsantragById(id: number): Promise<ZulassungsantragResponse> {
    const response = await apiClient.get(`${this.BASE_URL}/${id}`);
    return response.data;
  }

  // Neuen Zulassungsantrag erstellen
  static async createZulassungsantrag(formData: ZulassungsantragFormData): Promise<ZulassungsantragResponse> {
    // Direkte JSON-Übertragung der Formulardaten
    const requestData: ZulassungsantragCreateRequest = {
      nachrichtenId: this.generateNachrichtenId(),
      kunde: { id: formData.kundeId },
      nutzer: { id: formData.nutzerId! },
      status: formData.status || 'ERSTELLT',
      geschaeftsvorfallTyp: formData.geschaeftsvorfallTyp,
      antragstellerTyp: formData.antragstellerTyp,
      
      // Halterdaten
      halterTyp: formData.halterTyp,
      vorname: formData.vorname,
      nachname: formData.nachname,
      geburtstag: formData.geburtstag,
      geburtsort: formData.geburtsort,
      email: formData.email,
      telefonnummer: formData.telefonnummer,

      // Fahrzeugdaten
      fahrzeugTyp: formData.fahrzeugTyp,
      fin: formData.fin,
      zbIINummer: formData.zbIINummer,
      zbIISicherheitscode: formData.zbIISicherheitscode,
      reservierungsPin: formData.reservierungsPin,
      kennzeichenart: formData.kennzeichenart,
      elektrokennzeichen: formData.elektrokennzeichen,
      feinstaubplakette: formData.feinstaubplakette,

      // Bankverbindung
      zulassungsstelle: formData.zulassungsstelle,
      iban: formData.iban || '',
      bic: formData.bic || '',
      bankName: formData.bankName || '',
      bankVorname: formData.bankVorname || '',

      // HU-Nachweis
      huMonatJahr: formData.huMonatJahr,
      huPruefziffer: formData.huPruefziffer,
      huDatumUntersuchung: formData.huDatumUntersuchung,
      huUeberwachungsorganisation: formData.huUeberwachungsorganisation,

      // Versandadresse
      versandVorname: formData.versandVorname,
      versandName: formData.versandName,
      versandStrasse: formData.versandStrasse,
      versandHausnummer: formData.versandHausnummer,
      versandAdressierungszusaetze: formData.versandAdressierungszusaetze,
      versandPostleitzahl: formData.versandPostleitzahl,
      versandOrt: formData.versandOrt
    };

    const response = await apiClient.post(this.BASE_URL, requestData);
    return response.data;
  }

  // Zulassungsantrag aktualisieren
  static async updateZulassungsantrag(
    id: number, 
    formData: ZulassungsantragFormData
  ): Promise<ZulassungsantragResponse> {
    // Direkte JSON-Übertragung der Formulardaten
    const requestData: ZulassungsantragCreateRequest = {
      nachrichtenId: this.generateNachrichtenId(),
      kunde: { id: formData.kundeId },
      nutzer: { id: formData.nutzerId! },
      status: formData.status || 'ERSTELLT',
      geschaeftsvorfallTyp: formData.geschaeftsvorfallTyp,
      antragstellerTyp: formData.antragstellerTyp,
      
      // Halterdaten
      halterTyp: formData.halterTyp,
      vorname: formData.vorname,
      nachname: formData.nachname,
      geburtstag: formData.geburtstag,
      geburtsort: formData.geburtsort,
      email: formData.email,
      telefonnummer: formData.telefonnummer,

      // Fahrzeugdaten
      fahrzeugTyp: formData.fahrzeugTyp,
      fin: formData.fin,
      zbIINummer: formData.zbIINummer,
      zbIISicherheitscode: formData.zbIISicherheitscode,
      reservierungsPin: formData.reservierungsPin,
      kennzeichenart: formData.kennzeichenart,
      elektrokennzeichen: formData.elektrokennzeichen,
      feinstaubplakette: formData.feinstaubplakette,

      // Bankverbindung
      zulassungsstelle: formData.zulassungsstelle,
      iban: formData.iban || '',
      bic: formData.bic || '',
      bankName: formData.bankName || '',
      bankVorname: formData.bankVorname || '',

      // HU-Nachweis
      huMonatJahr: formData.huMonatJahr,
      huPruefziffer: formData.huPruefziffer,
      huDatumUntersuchung: formData.huDatumUntersuchung,
      huUeberwachungsorganisation: formData.huUeberwachungsorganisation,

      // Versandadresse
      versandVorname: formData.versandVorname,
      versandName: formData.versandName,
      versandStrasse: formData.versandStrasse,
      versandHausnummer: formData.versandHausnummer,
      versandAdressierungszusaetze: formData.versandAdressierungszusaetze,
      versandPostleitzahl: formData.versandPostleitzahl,
      versandOrt: formData.versandOrt
    };

    const response = await apiClient.put(`${this.BASE_URL}/${id}`, requestData);
    return response.data;
  }

  // Zulassungsantrag löschen
  static async deleteZulassungsantrag(id: number): Promise<void> {
    await apiClient.delete(`${this.BASE_URL}/${id}`);
  }

  // Zulassungsanträge nach Kunde filtern
  static async getZulassungsantraegeByKunde(
    kundeId: number,
    page: number = 0,
    size: number = 10
  ): Promise<PaginatedZulassungsantraegeResponse> {
    const response = await apiClient.get(`${this.BASE_URL}/kunde/${kundeId}`, {
      params: { page, size }
    });
    return response.data;
  }

  // Zulassungsanträge nach Status filtern
  static async getZulassungsantraegeByStatus(
    status: string,
    page: number = 0,
    size: number = 10
  ): Promise<PaginatedZulassungsantraegeResponse> {
    const response = await apiClient.get(`${this.BASE_URL}/status/${status}`, {
      params: { page, size }
    });
    return response.data;
  }

  // Antrag einreichen (an KBA senden) - gibt SubmitResult mit Quittung zurück
  // Timeout auf 180 Sekunden erhöht, da KBA-Kommunikation lange dauern kann
  static async submitZulassungsantrag(id: number): Promise<SubmitResult> {
    const response = await apiClient.post(`${this.BASE_URL}/${id}/submit`, null, {
      timeout: 180000 // 180 Sekunden
    });
    return response.data;
  }

  // KBA-Status abfragen (vorsystem.anfragestatus.0703)
  static async queryKbaStatus(id: number): Promise<StatusAbfrageResult> {
    const response = await apiClient.get(`${this.BASE_URL}/${id}/kba-status`);
    return response.data;
  }
  
  // Automatischen Workflow neu starten (bei Status FEHLER)
  static async restartWorkflow(id: number): Promise<{ message: string; antragId: number; neuerStatus: string }> {
    const response = await apiClient.post(`${this.BASE_URL}/${id}/restart-workflow`);
    return response.data;
  }

  // Antrag zurücksetzen (Status ändern)
  static async resetZulassungsantrag(id: number): Promise<ZulassungsantragResponse> {
    const response = await apiClient.post(`${this.BASE_URL}/${id}/reset`);
    return response.data;
  }

  // Antrag unterschreiben
  static async signZulassungsantrag(id: number): Promise<ZulassungsantragResponse> {
    const response = await apiClient.post(`${this.BASE_URL}/${id}/sign`);
    return response.data;
  }

  // Status aktualisieren
  static async updateStatus(id: number, status: string): Promise<ZulassungsantragResponse> {
    // Zuerst den aktuellen Antrag laden
    const currentAntrag = await this.getZulassungsantragById(id);
    
    // Komplettes Objekt mit neuem Status senden
    const updateData: ZulassungsantragCreateRequest = {
      nachrichtenId: currentAntrag.nachrichtenId,
      kunde: { id: currentAntrag.kunde.id },
      nutzer: { id: currentAntrag.nutzer?.id || 0 },
      status: status,
      geschaeftsvorfallTyp: currentAntrag.geschaeftsvorfallTyp,
      antragstellerTyp: currentAntrag.antragstellerTyp,
      
      // Halterdaten
      halterTyp: currentAntrag.halterTyp,
      vorname: currentAntrag.vorname,
      nachname: currentAntrag.nachname,
      geburtstag: currentAntrag.geburtstag,
      geburtsort: currentAntrag.geburtsort,
      email: currentAntrag.email,
      telefonnummer: currentAntrag.telefonnummer,

      // Fahrzeugdaten
      fahrzeugTyp: currentAntrag.fahrzeugTyp,
      fin: currentAntrag.fin,
      zbIINummer: currentAntrag.zbIINummer,
      zbIISicherheitscode: currentAntrag.zbIISicherheitscode,
      reservierungsPin: currentAntrag.reservierungsPin || '',
      kennzeichenart: currentAntrag.kennzeichenart || '',
      elektrokennzeichen: currentAntrag.elektrokennzeichen || false,
      feinstaubplakette: currentAntrag.feinstaubplakette || false,

      // Bankverbindung
      zulassungsstelle: currentAntrag.zulassungsstelle,
      iban: currentAntrag.iban || '',
      bic: currentAntrag.bic || '',
      bankName: currentAntrag.bankName || '',
      bankVorname: currentAntrag.bankVorname || '',

      // HU-Nachweis
      huMonatJahr: currentAntrag.huMonatJahr || '',
      huPruefziffer: currentAntrag.huPruefziffer || '',
      huDatumUntersuchung: currentAntrag.huDatumUntersuchung || '',
      huUeberwachungsorganisation: currentAntrag.huUeberwachungsorganisation || '',

      // Versandadresse
      versandVorname: currentAntrag.versandVorname || '',
      versandName: currentAntrag.versandName || '',
      versandStrasse: currentAntrag.versandStrasse || '',
      versandHausnummer: currentAntrag.versandHausnummer || '',
      versandAdressierungszusaetze: currentAntrag.versandAdressierungszusaetze || '',
      versandPostleitzahl: currentAntrag.versandPostleitzahl || '',
      versandOrt: currentAntrag.versandOrt || ''
    };
    
    const response = await apiClient.put(`${this.BASE_URL}/${id}`, updateData);
    return response.data;
  }

  // Hilfsfunktion zur Generierung einer Nachrichten-ID (UUID v4 Format)
  private static generateNachrichtenId(): string {
    // Generate a valid UUID v4
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    
    // Fallback for older browsers - generate UUID v4 manually
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  // Kennzeichenreservierung abrufen
  static async getKennzeichenReservierung(id: number): Promise<boolean> {
    const response = await apiClient.get(`${this.BASE_URL}/${id}/kennzeichen-reservierung`);
    return response.data.reservierung || false;
  }

  // Kennzeichenreservierung aktualisieren
  static async updateKennzeichenReservierung(id: number, reservierung: boolean): Promise<ZulassungsantragResponse> {
    const response = await apiClient.put(`${this.BASE_URL}/${id}/kennzeichen-reservierung`, {
      reservierung: reservierung
    });
    return response.data;
  }

  // Kennzeichenreservierung löschen (auf false setzen)
  static async deleteKennzeichenReservierung(id: number): Promise<void> {
    await apiClient.delete(`${this.BASE_URL}/${id}/kennzeichen-reservierung`);
  }

  // Validierung der Formulardaten vor dem Senden
  static validateFormData(formData: ZulassungsantragFormData): string[] {
    const errors: string[] = [];

    // Geschäftsvorfall prüfen
    if (!formData.geschaeftsvorfallTyp) errors.push('Geschäftsvorfall-Typ ist erforderlich');

    // Halterdaten prüfen
    if (!formData.vorname) errors.push('Vorname ist erforderlich');
    if (!formData.nachname) errors.push('Nachname ist erforderlich');
    
    // Fahrzeugdaten prüfen
    if (!formData.fin) errors.push('FIN ist erforderlich');
    if (!formData.zbIINummer) errors.push('ZB1 Nummer ist erforderlich');
    if (!formData.zbIISicherheitscode) errors.push('Sicherheitscode ZB1 ist erforderlich');
    
    // Bankverbindung prüfen
    if (!formData.zulassungsstelle) errors.push('Zulassungsstelle ist erforderlich');
    if (!formData.iban) errors.push('IBAN ist erforderlich');
    if (!formData.bankName) errors.push('Name des Kontoinhabers ist erforderlich');
    
    // Versandadresse prüfen
    if (!formData.versandName) errors.push('Name für Versandadresse ist erforderlich');
    if (!formData.versandStrasse) errors.push('Straße für Versandadresse ist erforderlich');
    if (!formData.versandHausnummer) errors.push('Hausnummer für Versandadresse ist erforderlich');
    if (!formData.versandPostleitzahl) errors.push('Postleitzahl für Versandadresse ist erforderlich');
    if (!formData.versandOrt) errors.push('Ort für Versandadresse ist erforderlich');
    if (!formData.kundeId || formData.kundeId === 0) errors.push('Kunde muss ausgewählt werden');

    // Format-Validierungen
    if (formData.fin && !/^[A-HJ-NPR-Z0-9]{17}$/.test(formData.fin)) {
      errors.push('FIN muss 17 Zeichen lang sein (ohne I, O, Q)');
    }

    if (formData.iban && !/^[A-Z]{2}[0-9]{2}[A-Z0-9]{4}[0-9]{7}([A-Z0-9]?){0,16}$/.test(formData.iban)) {
      errors.push('Ungültige IBAN');
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.push('Ungültige E-Mail-Adresse');
    }

    if (formData.versandPostleitzahl && !/^\d{5}$/.test(formData.versandPostleitzahl)) {
      errors.push('Postleitzahl muss 5 Ziffern haben');
    }

    if (formData.reservierungsPin && !/^\d{4,8}$/.test(formData.reservierungsPin)) {
      errors.push('Reservierungs-PIN muss 4-8 Ziffern haben');
    }

    if (formData.huPruefziffer && !/^\d{8}$/.test(formData.huPruefziffer)) {
      errors.push('HU-Prüfziffer muss 8 Ziffern haben');
    }

    return errors;
  }
}

export default ZulassungsantragService;
